import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Divider,
  Grid,
  IconButton,
} from "@mui/material";
import moment from "moment";
import { Link } from "react-router-dom";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { formatRate, getBookieLogoUrl } from "../../constants";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import { calculateKellyStake } from "../../utils/kelly";
import ToggleableDate from "../ToggleableDate";

const MobileValueCard = ({
  row,
  columnVisibilityModel,
  copyToBetTracker,
  kellySettings,
  hideBet,
  handleGroupedBetClick,
  evMode,
}) => {
  const theme = useTheme();

  const renderChip = (value, color = "default", size = "small") => (
    <Chip
      label={value}
      size={size}
      sx={{
        fontWeight: 600,
        backgroundColor: "transparent",
        border: `1px solid ${
          color === "default" ? (theme) => theme.palette.grey[300] : color
        }`,
        color: color === "default" ? "inherit" : color,
      }}
    />
  );

  const renderField = (value, formatter = (v) => v, align = "left") => (
    <Chip
      label={formatter(value)}
      size="small"
      sx={{
        fontWeight: 600,
        backgroundColor: "transparent",
        border: `1px solid ${(theme) => theme.palette.grey[300]}`,
        maxWidth: "100%",
        height: "auto",
        "& .MuiChip-label": {
          whiteSpace: "normal",
          wordWrap: "break-word",
          textAlign: align,
          justifyContent: align === "right" ? "flex-end" : "flex-start",
          padding: 0,
        },
      }}
    />
  );

  const renderEV = (value) => {
    let color = value >= 3 ? "#35BD5B" : value > 0 ? "#FFA500" : "#FF0000";
    return (
      <Chip
        label={`${value.toFixed(2)}%`}
        size="medium"
        sx={{
          fontWeight: 600,
          backgroundColor: "transparent",
          border: `1px solid ${color}`,
          color: color,
        }}
      />
    );
  };

  const renderKellyStakes = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="body2">
        1/4 Kelly:{" "}
        {calculateKellyStake(25, row.rate, row.fairOdds, kellySettings)}
      </Typography>
      <Typography variant="body2">
        1/2 Kelly:{" "}
        {calculateKellyStake(50, row.rate, row.fairOdds, kellySettings)}
      </Typography>
      <Typography variant="body2">
        Full Kelly:{" "}
        {calculateKellyStake(100, row.rate, row.fairOdds, kellySettings)}
      </Typography>
    </Box>
  );

  const renderSection = (title, fields, isLastSection = false) => {
    const visibleFields = fields.filter(
      ([field]) => columnVisibilityModel[field] !== false
    );
    if (visibleFields.length === 0) return null;

    return (
      <>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          {title}
        </Typography>
        <Grid container spacing={1}>
          {visibleFields.map(([field, label, value, formatter]) => (
            <Grid item xs={6} key={field}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {label}:
                </Typography>
                {renderChip(formatter ? formatter(value) : value)}
              </Box>
            </Grid>
          ))}
        </Grid>
        {!isLastSection && <Divider sx={{ my: 1 }} />}
      </>
    );
  };

  return (
    <Card sx={{ width: "100%" }}>
      <CardContent sx={{ p: 2 }}>
        {/* Top Row - Date and Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <ToggleableDate date={row.eventStart} defaultFormat="date" />
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              onClick={() => hideBet(row)}
              size="small"
              sx={{ color: "text.secondary" }}
            >
              <VisibilityOffIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              onClick={() => copyToBetTracker({ row })}
              size="small"
              sx={{ color: "text.secondary" }}
            >
              <ContentCopyIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Event Name */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: 1,
            color: "text.primary",
          }}
        >
          {row.key}
        </Typography>

        {/* Market */}
        <Typography
          sx={{
            fontSize: "0.875rem",
            color: "text.secondary",
            mb: 2,
          }}
        >
          {row.market}
        </Typography>

        {/* Bottom Row - EV and Bookie Info */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          {evMode && (
            <Typography
              sx={{
                color:
                  row.ev >= 3 ? "#35BD5B" : row.ev > 0 ? "#FFA500" : "#FF0000",
                fontWeight: 600,
                fontSize: "1rem",
              }}
            >
              EV: {row.ev.toFixed(1)}%
            </Typography>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              ml: evMode ? 0 : "auto",
            }}
          >
            {columnVisibilityModel.bookie !== false && row.bookie && (
              <img
                src={getBookieLogoUrl(row.bookie)}
                alt={row.bookie}
                style={{
                  maxWidth: "120px",
                  height: "32px",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
                onClick={() => window.open(row.source)}
              />
            )}
            <Box
              onClick={(e) => {
                e.stopPropagation();
                handleGroupedBetClick(row);
              }}
              sx={{
                backgroundColor: alpha(theme.palette.grey[700], 0.5),
                borderRadius: "9999px",
                px: 1.5,
                py: 0.5,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: "text.secondary",
                }}
              >
                Bookie count: {row.bookieCount}
              </Typography>
              <InfoOutlinedIcon
                sx={{
                  fontSize: 14,
                  color: "text.secondary",
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Event Link */}
        {columnVisibilityModel.event !== false && row.event && (
          <Button
            component={Link}
            to={`/event/${row.event_id}`}
            startIcon={<LinkIcon />}
            size="small"
            fullWidth
            variant="outlined"
            sx={{ mb: 1, textTransform: "none" }}
          >
            {row.event}
          </Button>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Odds */}
        {renderSection("Odds", [
          ["rate", "Odds", row.rate, formatRate],
          ["bestAltOdds", "Best Alt", row.bestAltOdds, formatRate],
          ["pinnacleFairOdds", "Pinnacle", row.pinnacleFairOdds, formatRate],
          ["aveOdds", "Avg Odds", row.aveOdds, formatRate],
          ...(evMode
            ? [["fairOdds", "Fair Odds", row.fairOdds, formatRate]]
            : []),
        ])}

        {/* Ratios */}
        {renderSection("Ratios", [
          ["oddsAveRatio", "Odds/Avg", row.oddsAveRatio, (v) => v.toFixed(2)],
          [
            "firstSecondRatio",
            "Odds/Alt",
            row.firstSecondRatio,
            (v) => v.toFixed(2),
          ],
          ...(evMode
            ? [
                [
                  "oddsFairRatio",
                  "Odds/Fair",
                  row.oddsFairRatio,
                  (v) => v.toFixed(2),
                ],
              ]
            : []),
        ])}

        {/* Kelly Criterion - Only show in EV mode */}
        {evMode &&
          renderSection(
            "Kelly Criterion",
            [
              [
                "quarterKelly",
                "1/4 Kelly",
                25,
                (v) =>
                  calculateKellyStake(v, row.rate, row.fairOdds, kellySettings),
              ],
              [
                "halfKelly",
                "1/2 Kelly",
                50,
                (v) =>
                  calculateKellyStake(v, row.rate, row.fairOdds, kellySettings),
              ],
              [
                "fullKelly",
                "Kelly",
                100,
                (v) =>
                  calculateKellyStake(v, row.rate, row.fairOdds, kellySettings),
              ],
            ],
            true
          )}
      </CardContent>
    </Card>
  );
};

export default React.memo(MobileValueCard);
